import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { deliveryKey, type DeliveryCompany, type DeliveryResponse, type EntryKind } from "./use-delivery-companies";

export interface EntryOptions extends DeliveryResponse {
  cashBalance: string; cashBoxId: string|null; notUsedCompanies: DeliveryCompany[];
}
export interface DeliveryEntryPayload {
  companyId: string; branchId: string; kind: EntryKind; amount: string;
  reason: string; photoDataUrl: string; settleCredit: boolean; idempotencyKey: string; previewToken?: string;
}
export interface EntryPreview extends Omit<DeliveryEntryPayload, "photoDataUrl" | "idempotencyKey"> {
  cashBefore: string; cashAfter: string; companyBefore: string; companyAfter: string;
  previewToken: string;
}
export interface EntrySaved extends EntryPreview { id: string; reference: string }
export const entriesEndpoint = (workspaceId: string, suffix: string) => `/api/delivery-companies/${suffix}?workspaceId=${encodeURIComponent(workspaceId)}`;
export const fetchDeliveryEntryOptions = async(workspaceId:string,branchId:string):Promise<EntryOptions> =>
  (await apiRequest("GET", `${entriesEndpoint(workspaceId,"entry-options")}&branchId=${encodeURIComponent(branchId)}`)).json();
export function useDeliveryEntryOptions(workspaceId: string, branchId: string) {
  return useQuery<EntryOptions>({
    queryKey: [...deliveryKey(workspaceId), "entry-options", branchId],
    queryFn: () => fetchDeliveryEntryOptions(workspaceId,branchId),
    enabled: !!workspaceId && !!branchId,
    staleTime: 0,
  });
}
export function useDeliveryEntryWrite(workspaceId: string) {
  const preview = useMutation({
    mutationFn: async (body: DeliveryEntryPayload): Promise<EntryPreview> =>
      (await apiRequest("POST", entriesEndpoint(workspaceId,"entries/preview"), body)).json(),
  });
  const save = useMutation({
    mutationFn: async (body: DeliveryEntryPayload): Promise<EntrySaved> =>
      (await apiRequest("POST", entriesEndpoint(workspaceId,"entries"), body)).json(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: deliveryKey(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: ["branch-cash"] }),
        queryClient.invalidateQueries({ queryKey: ["insights"] }),
      ]);
    },
  });
  return { preview, save };
}
export interface DeliveryCashRow {
  businessDate?:string;postingSequence?:string|number;expenseRecordLineNumber?:number;
  id:string;reference:string;companyId:string;companyName:string;companyNameAr:string;
  branchId:string;kind:"pay"|"charge"|"received";amount:string;reason:string;occurredAt:string;
  photoUrl:string;movementId:string|null;paymentId:string|null;
  cashBefore?:string;cashAfter?:string;
  createdByName?:string;
}
export interface DeliveryCashBookData { entries:DeliveryCashRow[];owedToBranch:string;owedToCompanies:string;cashBalance:string }
export function useDeliveryCashBook(workspaceId:string,branchId:string) {
  return useQuery<DeliveryCashBookData>({
    queryKey:[...deliveryKey(workspaceId),"cash-book",branchId],
    queryFn:async()=>(await apiRequest("GET",`${entriesEndpoint(workspaceId,"cash-book")}&branchId=${encodeURIComponent(branchId)}`)).json(),
    enabled:!!workspaceId&&!!branchId,
  });
}
